/* import __COLOCATED_TEMPLATE__ from './procedure-data-connector-health.hbs'; */
/* RESPONSIBLE TEAM: team-actions */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type AppService from 'embercom/services/app-service';
import type IntlService from 'ember-intl/services/intl';

export interface DataConnectorHealthEntry {
  action_id: number;
  name: string;
  health: {
    overall_status: string;
    success_rate: number;
    execution_count: number;
    response_size_metrics?: {
      response_body: { avg: number | null; p90: number | null; max: number | null };
      raw_response_body: { avg: number | null; p90: number | null; max: number | null };
    };
    external_latency_metrics?: {
      avg: number | null;
      p90: number | null;
    };
    failure_types_distribution?: Record<string, { count: number; percentage: number }>;
  };
}

interface TableRow {
  actionId: number;
  name: string;
  settingsUrl: string;
  statusLabel: string;
  statusStampColor: string;
  statusDescription: string;
  successRate: number;
  executionCount: number;
  avgResponseSize: string;
  p90Latency: string;
  topError: string;
  showSizeWarning: boolean;
}

interface ProcedureDataConnectorHealthSignature {
  Args: {
    dataConnectors: DataConnectorHealthEntry[];
    isLoading: boolean;
    procedureId: string;
  };
  Element: HTMLDivElement;
}

const LARGE_RESPONSE_WARNING_BYTES = 50_000;

function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) {
    return '\u2014';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatMs(ms: number | null | undefined): string {
  if (ms == null) {
    return '\u2014';
  }
  if (ms < 1000) {
    return `${ms}ms`;
  }
  return `${(ms / 1000).toFixed(1)}s`;
}

export default class ProcedureDataConnectorHealth extends Component<ProcedureDataConnectorHealthSignature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;

  get hasConnectors(): boolean {
    return this.args.dataConnectors.length > 0;
  }

  get tableRows(): TableRow[] {
    return this.args.dataConnectors.map((connector) => {
      let rawSize = connector.health.response_size_metrics?.raw_response_body;
      let latency = connector.health.external_latency_metrics;
      let avgRawBytes = rawSize?.avg;

      let topError = '\u2014';
      if (connector.health.failure_types_distribution) {
        let entries = Object.entries(connector.health.failure_types_distribution);
        if (entries.length > 0) {
          let [type, data] = entries[0];
          topError = `${type} (${data.count})`;
        }
      }

      return {
        actionId: connector.action_id,
        name: connector.name,
        settingsUrl: `/a/apps/${this.appService.app.id}/settings/app-settings/data-connectors/${connector.action_id}`,
        statusLabel: connector.health.overall_status,
        statusStampColor: this.stampColor(connector.health.overall_status),
        statusDescription: this.statusDescription(connector.health.overall_status),
        successRate: connector.health.success_rate,
        executionCount: connector.health.execution_count,
        avgResponseSize: formatBytes(avgRawBytes),
        p90Latency: formatMs(latency?.p90),
        topError,
        showSizeWarning: avgRawBytes != null && avgRawBytes >= LARGE_RESPONSE_WARNING_BYTES,
      };
    });
  }

  private stampColor(status: string): string {
    switch (status) {
      case 'healthy':
        return 'green';
      case 'degraded':
        return 'yellow';
      case 'unhealthy':
        return 'red';
      default:
        return 'gray';
    }
  }

  private statusDescription(status: string): string {
    switch (status) {
      case 'healthy':
        return this.intl.t(
          'standalone.tasks.list.procedure-stats.data-connector-health.status-healthy',
        );
      case 'degraded':
        return this.intl.t(
          'standalone.tasks.list.procedure-stats.data-connector-health.status-degraded',
        );
      case 'unhealthy':
        return this.intl.t(
          'standalone.tasks.list.procedure-stats.data-connector-health.status-unhealthy',
        );
      default:
        return '';
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'TasksAndProcedures::ProcedureDataConnectorHealth': typeof ProcedureDataConnectorHealth;
    'tasks-and-procedures/procedure-data-connector-health': typeof ProcedureDataConnectorHealth;
  }
}
